import Loader from "components/Loader/loader"
import React, { useEffect, useState } from "react"
import { FaPlus, FaEdit } from "react-icons/fa"
import { MdCancel, MdDelete, MdEdit } from "react-icons/md"
import Select from "react-select"
import {
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Input,
  Table,
  Row,
  Col,
  FormFeedback,
  Label,
  Card,
  CardBody,
} from "reactstrap"
import Swal from "sweetalert2"
import {
  fetchData,
  FetchDeleteData,
  FetchPostData,
  FetchPutData,
} from "utils/Common_api_functions"

const customSelectStyles = {
  control: (styles, { isFocused }) => ({
    ...styles,
    borderRadius: "20px",
    padding: "2px 4px",
    borderColor: isFocused ? "#007BFF" : "#ced4da",
    boxShadow: isFocused ? "0 0 0 0.2rem rgba(0, 123, 255, 0.25)" : "none",
    "&:hover": { borderColor: "#007BFF" },
  }),
  option: (styles, { isFocused, isSelected }) => ({
    ...styles,
    backgroundColor: isSelected ? "#007BFF" : isFocused ? "#F1F1F1" : "white",
    color: isSelected ? "white" : "#333",
    padding: "10px 8px",
    cursor: "pointer",
    borderRadius: "20px",
  }),
  menu: styles => ({
    ...styles,
    backgroundColor: "#fff",
  }),
  singleValue: styles => ({ ...styles, color: "#333" }),
}

const formatTo12Hour = time => {
  if (!time) return ""
  const [hour, minute] = time.split(":")
  let h = parseInt(hour)
  const ampm = h >= 12 ? "PM" : "AM"
  h = h % 12 || 12
  return `${h}:${minute} ${ampm}`
}

const calculateTotalTime = (inTime, outTime) => {
  if (!inTime || !outTime) return "0h 0m"
  const [inH, inM] = inTime.split(":").map(Number)
  const [outH, outM] = outTime.split(":").map(Number)

  let totalMinutes = outH * 60 + outM - (inH * 60 + inM)
  if (totalMinutes < 0) totalMinutes += 24 * 60 // handle overnight (unlikely here)

  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60

  return `${hours}h ${minutes}m`
}

const LocationTimeModal = ({
  isOpen,
  toggle,
  selectedLocation,
  locationName,
}) => {
  const [day, setday] = useState("")
  const [inTime, setInTime] = useState("")
  const [outTime, setOutTime] = useState("")
  const [slots, setSlots] = useState([])
  const [uniqueEmployees, setUniqueEmployees] = useState([])
  const [editId, setEditId] = useState(null)
  const [employees, setEmployees] = useState([])

  const [errors, setErrors] = useState({
    day: "",
    inTime: "",
    outTime: "",
    employees: "",
  })

  const [loading, setloading] = useState(false)

  useEffect(() => {
    if (selectedLocation) {
      getLocationSlots()
    }
  }, [selectedLocation])

  async function getLocationSlots() {
    setloading(true)
    const APIdata = await fetchData(setloading, `slot/${selectedLocation}`)
    if (APIdata?.success) {
      setSlots(APIdata?.data || [])
    } else {
      setSlots([])
    }
    setloading(false)
  }

  useEffect(() => {
    if (day && inTime && outTime) {
      getLocationEmployees()
    } else {
      setUniqueEmployees([])
    }
  }, [day, inTime, outTime])

  async function getLocationEmployees() {
    const APIdata = await FetchPostData(
      setloading,
      `slot/available/employees`,
      {
        day,
        inTime,
        outTime,
        locationId: selectedLocation,
      }
    )
    if (APIdata?.data?.success) {
      setUniqueEmployees(APIdata?.data?.employees || [])
    }
  }

  const validateInputs = () => {
    const newErrors = {}

    if (!day) {
      newErrors.day = "Please select a day"
    }

    if (!inTime) {
      newErrors.inTime = "Please select in time"
    }

    if (!outTime) {
      newErrors.outTime = "Please select out time"
    } else if (inTime && outTime <= inTime) {
      newErrors.outTime = "Out time must be after in time"
    }

    if (!employees || employees.length === 0) {
      newErrors.employees = "Please select at least one employee"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleAddOrUpdateSlot = async () => {
    if (!validateInputs()) {
      return
    }

    const payload = {
      day,
      inTime,
      outTime,
      employees: JSON.stringify(employees.map(e => e.value)),
    }

    try {
      let APIData
      if (editId) {
        APIData = await FetchPutData(setloading, "slot", editId, payload)
      } else {
        APIData = await FetchPostData(setloading, "slot", {
          ...payload,
          locationId: selectedLocation,
        })
      }

      if (APIData?.data?.success) {
        Swal.fire({
          title: editId ? "Slot Updated!" : "Slot Added!",
          text: "Location time slot saved successfully.",
          icon: "success",
          timer: 1500,
        })
        setSlots(APIData?.data?.allSlots || [])
        resetForm()
      } else {
        throw new Error(APIData?.response?.data?.message || "Unknown error")
      }
    } catch (err) {
      Swal.fire({
        title: "Error",
        text: err.message || "Failed to save slot. Please try again.",
        icon: "error",
      })
    }
  }

  const resetForm = () => {
    setday("")
    setInTime("")
    setOutTime("")
    setEmployees([])
    setEditId(null)
    setErrors({ day: "", inTime: "", outTime: "", employees: "" })
  }

  const handleRemoveSlot = async id => {
    const result = await Swal.fire({
      title: "Delete Slot?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
    })

    if (result.isConfirmed) {
      const APIdata = await FetchDeleteData(setloading, "slot", id)
      if (APIdata?.data?.success) {
        Swal.fire("Deleted!", "Slot removed successfully.", "success")
        setSlots(APIdata?.data?.allSlots || [])
      }
    }
  }

  const handleEditSlot = slot => {
    setday(slot.day)
    setInTime(slot.inTime)
    setOutTime(slot.outTime)
    setEditId(slot.id)
    setEmployees(
      slot?.employees?.map(emp => ({
        value: emp.id,
        label: emp.name,
      })) || []
    )
    setErrors({}) // Clear errors on edit
  }

  const handleCancelEdit = () => {
    resetForm()
  }

  const days = [
    { label: "Monday", value: 1 },
    { label: "Tuesday", value: 2 },
    { label: "Wednesday", value: 3 },
    { label: "Thursday", value: 4 },
    { label: "Friday", value: 5 },
    { label: "Saturday", value: 6 },
    { label: "Sunday", value: 0 },
  ]

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="xl">
      {loading && <Loader />}
      <ModalHeader toggle={toggle}>
        Set Location Time <span className="fw-bold ms-3">({locationName})</span>
      </ModalHeader>
      <ModalBody>
        <Card className="mb-0 shadow-sm">
          <CardBody className="p-4">
            <h5 className="mb-4 text-primary">
              {editId ? "Edit Time Slot" : "Add New Time Slot"}
            </h5>

            <Row className="align-items-end g-3">
              <Col md="3">
                <Label className="fw-medium">
                  Day <span className="text-danger">*</span>
                </Label>
                <select
                  value={day}
                  onChange={e => {
                    setday(e.target.value)
                    if (errors.day) setErrors({ ...errors, day: "" })
                  }}
                  className={`form-select rounded-4 ${
                    errors.day ? "is-invalid" : ""
                  }`}
                >
                  <option value="">Select day</option>
                  {days.map(d => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </select>
                {errors.day && <FormFeedback>{errors.day}</FormFeedback>}
              </Col>

              <Col md="2">
                <Label className="fw-medium">
                  In Time <span className="text-danger">*</span>
                </Label>
                <Input
                  type="time"
                  value={inTime}
                  disabled={editId !== null}
                  invalid={!!errors.inTime}
                  onChange={e => {
                    setInTime(e.target.value)
                    setEmployees([])
                    if (errors.inTime) setErrors({ ...errors, inTime: "" })
                  }}
                />
                {errors.inTime && <FormFeedback>{errors.inTime}</FormFeedback>}
              </Col>

              <Col md="2">
                <Label className="fw-medium">
                  Out Time <span className="text-danger">*</span>
                </Label>
                <Input
                  type="time"
                  value={outTime}
                  disabled={editId !== null}
                  invalid={!!errors.outTime}
                  onChange={e => {
                    setOutTime(e.target.value)
                    setEmployees([])
                    if (errors.outTime) setErrors({ ...errors, outTime: "" })
                  }}
                />
                {errors.outTime && (
                  <FormFeedback>{errors.outTime}</FormFeedback>
                )}
              </Col>

              <Col md="3">
                <Label className="fw-medium">
                  Employees <span className="text-danger">*</span>
                </Label>
                <Select
                  isMulti
                  styles={customSelectStyles}
                  value={employees}
                  onChange={option => {
                    setEmployees(option || [])
                    if (errors.employees)
                      setErrors({ ...errors, employees: "" })
                  }}
                  options={uniqueEmployees.map(emp => ({
                    value: emp?.id,
                    label: emp?.name,
                  }))}
                  placeholder="Select employees..."
                  className={errors.employees ? "is-invalid" : ""}
                />
                {errors.employees && (
                  <small className="text-danger d-block mt-1">
                    {errors.employees}
                  </small>
                )}
              </Col>

              <Col md="2">
                <div className="d-flex gap-2">
                  <Button
                    color={editId ? "warning" : "success"}
                    onClick={handleAddOrUpdateSlot}
                    disabled={loading}
                  >
                    {editId ? <MdEdit size={16} /> : <FaPlus size={16} />}
                    <span className="ms-1">{editId ? "Update" : "Add"}</span>
                  </Button>
                  {editId && (
                    <Button color="secondary" onClick={handleCancelEdit}>
                      <MdCancel size={18} />
                    </Button>
                  )}
                </div>
              </Col>
            </Row>

            <hr className="my-4" />

            <h5 className="mb-3 text-primary">Existing Time Slots</h5>
            <div style={{ maxHeight: "300px", overflowY: "auto" }}>
              <Table bordered hover responsive size="sm">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Day</th>
                    <th>In Time</th>
                    <th>Out Time</th>
                    <th>Duration</th>
                    <th>Employees</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {slots.length > 0 ? (
                    slots.map((slot, index) => (
                      <tr key={slot.id}>
                        <td>{index + 1}</td>
                        <td>{days.find(d => d.value === slot.day)?.label}</td>
                        <td>{formatTo12Hour(slot.inTime)}</td>
                        <td>{formatTo12Hour(slot.outTime)}</td>
                        <td>{calculateTotalTime(slot.inTime, slot.outTime)}</td>
                        <td>
                          {slot?.employees?.map(e => e.name).join(", ") || "-"}
                        </td>
                        <td>
                          <Button
                            color="info"
                            size="sm"
                            className="me-2"
                            onClick={() => handleEditSlot(slot)}
                          >
                            <FaEdit size={14} />
                          </Button>
                          <Button
                            color="danger"
                            size="sm"
                            onClick={() => handleRemoveSlot(slot.id)}
                          >
                            <MdDelete size={14} />
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center text-muted py-3">
                        No time slots added yet for this location.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>
      </ModalBody>
    </Modal>
  )
}

export default LocationTimeModal
